import { colord } from "colord";

/**
 * Pivot the enriched topic-clustering tree into the donut's 4-level
 * hierarchy, observation by observation:
 *
 *   signal category → signal → topic bucket → finding (encapsulation)
 *
 * The clustering tree is topic-first (bucket → encapsulation → observation);
 * here each observation is re-filed under its own signal first, so one bucket
 * can appear under several signals — with only its findings that carry that
 * signal beneath it. Findings are the leaves, weighted by how many of their
 * observations carry the path's signal, so segment sizes are observation
 * counts all the way up. Each finding leaf keeps a reference to its ORIGINAL
 * enriched tree row, so clicking it opens the same annotation side panel
 * (with the row's full observation list) as the cards/table layouts.
 *
 * Node ids are path-scoped (`cat:x|s:y|b:z|…`) because the same
 * bucket/finding recurs under different signals and the chart's hover/reveal
 * tweens key by id. The `row` stashed on each leaf rides through the chart
 * untouched and comes back in its onLeafClick.
 */

// Ring-1 render order: the tenant's categories (same list and colours the
// card layout uses, via the signal-category theme), then a trailing bucket
// for observations whose annotation carries no signal tag, so they're
// shown rather than dropped.
const UNSIGNALLED = { category: "unsignalled", title: "No Signal" };
const categoryRing = (categoryTheme) => [
  ...categoryTheme.list.map((c) => ({ category: c.value, title: c.display })),
  UNSIGNALLED,
];

// Backstop only — the chart word-wraps labels to two lines and ellipsizes
// there; this just keeps pathological titles from being measured at length.
const truncate = (s, n = 70) => {
  const t = (s || "").trim();
  return t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t;
};

// Signals inside a category read as graded tints of the category colour, so
// ring 2 stays visually "of" its parent instead of going rainbow. (lighten is
// core colord; .mix() would need the plugin.) The unsignalled ring falls to
// the theme's default grey.
const signalTint = (categoryTheme, category, index) =>
  colord(categoryTheme.fillOf(category))
    .lighten(Math.min(0.05 + index * 0.05, 0.28))
    .toHex();

// 3-level trees drill into their children; legacy flat docs treat the bucket
// itself as its single encapsulation row (same convention as the layouts).
const rowsForBucket = (bucket) => (bucket.children?.length ? bucket.children : [bucket]);

const weightOf = (node) =>
  node.children?.length ? node.children.reduce((t, c) => t + weightOf(c), 0) : node.value;

const byWeightDesc = (a, b) => weightOf(b) - weightOf(a);

export const buildDonutData = (tree, signalMetaById, categoryTheme) => {
  // category → signal → bucket → encapsulation → [observation]. Maps keep
  // first-seen order; the materialise pass below re-sorts by weight.
  const byCategory = new Map();

  for (const bucket of tree?.tree || []) {
    for (const row of rowsForBucket(bucket)) {
      for (const o of row.observations || []) {
        const meta = o.signalId ? signalMetaById[o.signalId] : null;
        const category = meta?.category || "unsignalled";
        const signalKey = o.signalId || "none";

        if (!byCategory.has(category)) byCategory.set(category, new Map());
        const signals = byCategory.get(category);
        if (!signals.has(signalKey)) {
          signals.set(signalKey, { display: meta?.display || "No signal", buckets: new Map() });
        }
        const buckets = signals.get(signalKey).buckets;
        const bucketKey = String(bucket.nodeId);
        if (!buckets.has(bucketKey)) buckets.set(bucketKey, { bucket, rows: new Map() });
        const rows = buckets.get(bucketKey).rows;
        const rowKey = String(row.nodeId);
        if (!rows.has(rowKey)) rows.set(rowKey, { row, observations: [] });
        rows.get(rowKey).observations.push(o);
      }
    }
  }

  const children = categoryRing(categoryTheme).filter(({ category }) => byCategory.has(category)).map(
    ({ category, title }) => {
      const signalNodes = [...byCategory.get(category).entries()]
          .map(([signalKey, { display, buckets }]) =>
            ({
              id: `cat:${category}|s:${signalKey}`,
              label: truncate(display),
              observations: [...buckets.values()].flatMap(({ rows }) =>
                [...rows.values()].flatMap((r) => r.observations)
              ),
              children: [...buckets.values()]
                .map(({ bucket, rows }) =>
                  ({
                    id: `cat:${category}|s:${signalKey}|b:${bucket.nodeId}`,
                    label: truncate(bucket.label),
                    observations: [...rows.values()].flatMap((r) => r.observations),
                    children: [...rows.values()]
                      .map(({ row, observations }) =>
                        // Leaf: the finding, weighted by its observations
                        // that carry this path's signal. `row` is the
                        // original enriched tree node — the click hands it
                        // to the shared side panel untouched.
                        ({
                          id: `cat:${category}|s:${signalKey}|b:${bucket.nodeId}|e:${row.nodeId}`,
                          label: truncate(row.label),
                          value: observations.length,
                          // Path-filtered observations (only this signal's),
                          // so the level panel's counts and sparklines agree
                          // with the segment weights. Parents concat below.
                          observations,
                          row,
                        })
                      )
                      .sort(byWeightDesc),
                  })
                )
                .sort(byWeightDesc),
            })
          )
          .sort(byWeightDesc);
      // Tints grade with the sorted order, biggest signal closest to the
      // category colour.
      signalNodes.forEach((n, i) => (n.color = signalTint(categoryTheme, category, i)));

      return ({
        id: `cat:${category}`,
        label: title,
        color: categoryTheme.fillOf(category),
        observations: signalNodes.flatMap((n) => n.observations),
        children: signalNodes,
      });
    }
  );

  return { children };
};
